import { Download, Filter, Info, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { listAuditEvents, AuditApiError } from '../../../api/audit';
import SettingsPageShell from '../SettingsPageShell';

import { toAuditRowFromAction, type AuditRow } from './auditViewModel';
import AuditSidebar from './components/AuditSidebar';
import AuditTable from './components/AuditTable';
import UsersKpiStrip from './components/UsersKpiStrip';

/** Action filter options. `''` = all actions. Values map 1:1 to the
 *  backend's `action` query param (which matches user-management.service's
 *  stamped action names). */
const ACTION_OPTIONS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'All actions', value: '' },
  { label: 'Invited', value: 'invited' },
  { label: 'Role changed', value: 'role_changed' },
  { label: 'Regions changed', value: 'regions_changed' },
  { label: 'Deactivated', value: 'deactivated' },
  { label: 'Reactivated', value: 'reactivated' },
];

const PAGE_SIZE = 100;

function summarizeKpis(rows: AuditRow[]) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sevenAgo = new Date(today.getTime() - 7 * 86_400_000);

  const todayCount = rows.filter((r) => r.changedAt >= today).length;
  const weekCount = rows.filter((r) => r.changedAt >= sevenAgo).length;
  const actors = new Set(rows.map((r) => r.actor)).size;
  const invites = rows.filter((r) => r.action === 'invited').length;

  return [
    { label: 'Events today', value: todayCount, sub: 'across the tenant' },
    { label: 'Last 7 days', value: weekCount, sub: 'changes recorded' },
    { label: 'Active actors', value: actors, sub: 'distinct people' },
    { label: 'Invites sent', value: invites, sub: 'in window' },
  ];
}

interface FetchState {
  rows: AuditRow[] | null;
  error: string | null;
  forbidden: boolean;
}

/** Fetch the first page of audit events whenever the action filter changes.
 *  Extracted from the page component to keep the render function short. */
function useAuditEvents(actionFilter: string): FetchState {
  const [state, setState] = useState<FetchState>({ rows: null, error: null, forbidden: false });

  useEffect(() => {
    let cancelled = false;
    setState({ rows: null, error: null, forbidden: false });
    listAuditEvents({ limit: PAGE_SIZE, action: actionFilter || undefined })
      .then((page) => {
        if (cancelled) return;
        setState({ rows: page.events.map(toAuditRowFromAction), error: null, forbidden: false });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof AuditApiError && err.status === 403) {
          setState({ rows: null, error: null, forbidden: true });
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to load audit log';
        setState({ rows: null, error: message, forbidden: false });
      });
    return () => {
      cancelled = true;
    };
  }, [actionFilter]);

  return state;
}

/** Audit log — chronological view of every UserEditEvent.
 *
 *  Backed by GET /api/v1/audit/users (BON-56). High-signal-only by default
 *  (events with a stamped `action`); filterable to a single action. No
 *  pagination UI yet — we fetch the first {PAGE_SIZE} events. Forbidden
 *  callers see the dedicated permission message. */
export default function AuditPage() {
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const { rows, error, forbidden } = useAuditEvents(actionFilter);

  const visible = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.actor} ${r.target} ${r.action} ${r.field ?? ''}`.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const kpis = useMemo(() => summarizeKpis(rows ?? []), [rows]);

  if (forbidden) {
    return (
      <div className="text-sm text-stone-600 dark:text-stone-400 py-12 text-center">
        You don&apos;t have permission to view the audit log.
      </div>
    );
  }

  return (
    <SettingsPageShell
      crumb="Settings · Bonsai Bar"
      title="Audit log"
      actions={
        <button
          type="button"
          disabled
          title="CSV export ships in a follow-up"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-semibold text-stone-400 dark:text-stone-500 cursor-not-allowed"
        >
          <Download size={13} />
          Export CSV
        </button>
      }
    >
      {error && (
        <div className="bg-clay-50 dark:bg-clay-900/20 border border-clay-200 dark:border-clay-800 text-clay-700 dark:text-clay-300 rounded-lg p-4 text-sm">
          Couldn&apos;t load audit log: {error}
        </div>
      )}

      {!rows && !error && (
        <div className="text-sm text-stone-500 dark:text-stone-400 py-12 text-center">
          Loading audit log…
        </div>
      )}

      {rows && (
        <Loaded
          rows={visible}
          totalLoaded={rows.length}
          kpis={kpis}
          search={search}
          onSearch={setSearch}
          actionFilter={actionFilter}
          onActionFilterChange={setActionFilter}
        />
      )}
    </SettingsPageShell>
  );
}

function Loaded({
  rows,
  totalLoaded,
  kpis,
  search,
  onSearch,
  actionFilter,
  onActionFilterChange,
}: {
  rows: AuditRow[];
  totalLoaded: number;
  kpis: ReturnType<typeof summarizeKpis>;
  search: string;
  onSearch: (s: string) => void;
  actionFilter: string;
  onActionFilterChange: (s: string) => void;
}) {
  return (
    <>
      <UsersKpiStrip items={kpis} />
      <div className="flex-1 min-h-0 grid grid-cols-[1fr_280px] gap-3.5 overflow-hidden">
        <section className="bg-white dark:bg-[#1a1f1c] rounded-xl border border-stone-200 dark:border-stone-800 flex flex-col">
          <FilterBar
            count={rows.length}
            search={search}
            onSearch={onSearch}
            actionFilter={actionFilter}
            onActionFilterChange={onActionFilterChange}
          />
          <AuditTable rows={rows} />
          <div className="py-2 px-[18px] border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-[#1a1f1c] flex items-center text-[11.5px] text-stone-500 dark:text-stone-400">
            <Info size={12} />
            <span className="ml-1.5">
              Append-only. We log high-signal events: role, region, status, invites and FH links.
            </span>
            <div className="flex-1" />
            <span>Showing first {totalLoaded} events · refresh to update</span>
          </div>
        </section>
        <AuditSidebar rows={rows} />
      </div>
    </>
  );
}

function FilterBar({
  count,
  search,
  onSearch,
  actionFilter,
  onActionFilterChange,
}: {
  count: number;
  search: string;
  onSearch: (s: string) => void;
  actionFilter: string;
  onActionFilterChange: (s: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 py-2.5 pl-[18px] pr-3.5 border-b border-stone-200 dark:border-stone-800">
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500 dark:text-stone-400 pointer-events-none">
          <Search size={14} />
        </span>
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search target, actor, action…"
          className="w-[240px] h-8 pl-8 pr-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-[#1a1f1c] text-[13px] outline-none focus:ring-2 focus:ring-moss-500 focus:ring-offset-1"
        />
      </div>
      <div className="relative inline-flex items-center">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500 dark:text-stone-400 pointer-events-none">
          <Filter size={13} />
        </span>
        <select
          value={actionFilter}
          onChange={(e) => onActionFilterChange(e.target.value)}
          aria-label="Filter by action"
          className="h-8 pl-7 pr-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-[#1a1f1c] text-[12.5px] text-stone-700 dark:text-stone-300 outline-none focus:ring-2 focus:ring-moss-500 focus:ring-offset-1"
        >
          {ACTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex-1" />
      <span className="text-[11px] text-stone-500 dark:text-stone-400">
        Showing <strong className="text-bark-800 dark:text-[#e8ece9]">{count}</strong> events
      </span>
    </div>
  );
}
